/**
 * coverage-lag.core — pure, framework-free late-coverage logic (A9a).
 *
 * Wave-1 freshness means older-but-newsworthy tweets can be covered late, and a
 * breaking_news doc's `publishedAt` is BACKDATED to the source tweet's post time by
 * design (see storeDraft). That keeps the timeline honest about WHEN the event
 * happened — but it can look like we silently backdated when coverage actually lagged.
 *
 * This module computes the trust-preserving complement: when the wall-clock coverage
 * time lags the source tweet by MORE than 24h, it returns two short date labels so the
 * UI can say, plainly, "Posted Jun 6 — covered Jun 9". Otherwise it returns null and
 * the surfaces render nothing (the normal fresh path is byte-identical).
 *
 * Pure + deterministic: no Firestore, no React, no clock reads. This is the SINGLE
 * source of truth; `coverage-lag.ts` re-exports it with types for the React surfaces,
 * and `backend/scripts/test-a9a-late-label.js` requires it directly (so it runs under
 * plain node alongside the other backend suites).
 *
 * Authored as plain JS (not TS) on purpose: the acceptance test is a plain-node script
 * matching the house pattern of the 14 regression suites; a .js core needs no compile
 * step to be required there, while the .ts wrapper gives the frontend full typing.
 */

'use strict';

/**
 * Late-coverage threshold. Coverage lagging the source tweet by MORE than this
 * (strictly greater — 24h exactly is still "fresh") triggers the label.
 *
 * A hardcoded named constant, NOT operator-tunable config: this is display-only
 * trust copy, not a pipeline gate. Making it Firestore-tunable would let the labelled
 * window drift silently and is pure surface area for no benefit. 24h is the natural
 * "covered the same day or next morning reads as fresh; older needs to be said out
 * loud" line, and it matches the daily-recap cadence.
 */
const LATE_COVERAGE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Coerce a Firestore Timestamp (client `{ toDate() }` or admin `{ _seconds }`),
 * a Date, an ISO/parseable string, or an epoch number into a valid Date — or null.
 * Never throws on any input (including objects whose `toDate()` lies).
 *
 * @param {*} value
 * @returns {Date|null}
 */
function toDateOrNull(value) {
  if (value == null) return null;
  try {
    // Already a Date.
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    // Firestore client Timestamp.
    if (typeof value === 'object' && typeof value.toDate === 'function') {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    }
    // Firestore admin Timestamp ({ _seconds }).
    if (typeof value === 'object' && typeof value._seconds === 'number') {
      const d = new Date(value._seconds * 1000);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    // ISO string / epoch number.
    if (typeof value === 'string' || typeof value === 'number') {
      if (value === '') return null;
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  } catch (_err) {
    // Any coercion failure → treat as no date (silent, never throws).
    return null;
  }
  return null;
}

/**
 * Format a Date as a short "Mon D" label, appending ", YYYY" only when the two
 * dates being compared fall in different calendar years (UTC).
 *
 * @param {Date} date
 * @param {boolean} includeYear
 * @returns {string}
 */
function formatLabel(date, includeYear) {
  const month = MONTHS[date.getUTCMonth()];
  const day = date.getUTCDate();
  return includeYear ? `${month} ${day}, ${date.getUTCFullYear()}` : `${month} ${day}`;
}

/**
 * Given the source-tweet time and the wall-clock coverage time, return the
 * late-coverage labels — or null when coverage was NOT materially late.
 *
 * Returns null (silently, never throwing) when:
 *   - either input is missing / unparseable (e.g. a doc that predates any source-date
 *     field), or
 *   - the lag is <= 24h (the normal fresh path — including coverage that predates the
 *     tweet, i.e. negative lag).
 *
 * @param {*} sourceTweetTime  source tweet post time (Timestamp | Date | ISO | number)
 * @param {*} coverageTime     wall-clock coverage time (Timestamp | Date | ISO | number)
 * @returns {{ postedLabel: string, coveredLabel: string } | null}
 */
function computeCoverageLag(sourceTweetTime, coverageTime) {
  const posted = toDateOrNull(sourceTweetTime);
  const covered = toDateOrNull(coverageTime);
  if (!posted || !covered) return null;

  const lagMs = covered.getTime() - posted.getTime();
  if (lagMs <= LATE_COVERAGE_THRESHOLD_MS) return null;

  const includeYear = posted.getUTCFullYear() !== covered.getUTCFullYear();
  return {
    postedLabel: formatLabel(posted, includeYear),
    coveredLabel: formatLabel(covered, includeYear),
  };
}

module.exports = {
  computeCoverageLag,
  toDateOrNull,
  LATE_COVERAGE_THRESHOLD_MS,
};
